// FLAG:useDocuAi — resolve values for the review save where the reviewer must be able to CLEAR the
// field. This is reachable exclusively through `buildDocumentInvoiceDetails` in receipt-edit.tsx,
// which runs only in review mode (`documentReviewFileId`, set solely by the three review hosts) —
// the code is therefore unreachable with the flag OFF by construction, not by audit.
//
// The trigger was the IBAN: an invalid extracted IBAN makes the receipt impossible to save
// (`editFile.js` validateBody checks `documentInvoiceDetails.iban` with `isIbanAllowNull` and
// rejects the ENTIRE review save with NO_IBAN), and the only remedy is to clear the field.
// `buildDocumentInvoiceDetails` built the value as `data.iban || previousDetails.iban ||
// ocr.payeeIban`, however — a cleared field returns `null`, so the chain restored the ingested
// (invalid) IBAN and the save failed again even though the field appeared empty. This is not an
// edge case: on a direct-debit invoice DocuAI deliberately returns NO payee account
// (`target_reason: "none_direct_debit"`) but still guesses an IBAN in
// `payment_account_resolution.selected_iban` — the reviewer is right to delete it.
//
// The same trap existed in the other payment values (paymentStatus, openAmount,
// paymentMethodCode, paymentCardNumber,
// paymentCardScheme): their chain used `??`, which is no
// better — see below. Setting the paid status to "no information" did not survive the next load.
//
// **`??` does NOT solve this.** The nullish operator falls back for `null` just as it does for
// `undefined`, and the cleared input IS `null` (`onChange({ iban: value || null })`). Only the
// distinction between "key absent" (= undefined) and "reviewer cleared it" (= null) expresses the
// rule. `??` works for `payeeIbans`/`debitIbans` because their cleared value is `[]` — not nullish.
//
// Server-side counterpart: `fileService/update.js` resolves the IBAN for master data using the
// same `!== undefined` rule.

// Empty means no value. **0 is a value** — `openAmount: 0` means "nothing outstanding", not "not
// filled in"; that is exactly why the old code used `??` rather than `||`.
const isEmptyReviewValue = (value: unknown) => value === undefined || value === null || value === ''

export const resolveClearableReviewValue = <T>(
  formValue: T | null | undefined,
  ...fallbacks: (T | null | undefined)[]
): T | null => {
  // The reviewer owns the field as soon as it exists in the form — even when cleared.
  if (formValue !== undefined) return isEmptyReviewValue(formValue) ? null : (formValue as T)
  return (fallbacks.find(value => !isEmptyReviewValue(value)) ?? null) as T | null
}
